// Linking a new device to my space by QR code (docs/adr/0004 § 4).
//
// Host (a device already in the space) shows `#/link/<secret>`; the new device opens it. Both meet
// in the room of that one-time secret and authenticate with the same DTLS-bound proof as share
// links. The newcomer sends its public keys; the host shows its name and a short code and asks
// the user to confirm; on yes it seals the space secrets for the newcomer's X25519 key and signs
// its admission (device.add) into the journal. The newcomer then syncs like any device.

import { Mesh } from '../core/mesh';
import { deriveAuthKey } from '../core/auth';
import { randomSecret, roomIdFor } from '../core/ids';
import { createDevice, openSealed, publicRecord, sealFor, type DeviceRecord, type LocalDevice, type SpaceSecrets } from './keys';
import type { Space } from './space';
import { safeLabel } from '../drop/protocol';

const LINK_TTL_MS = 10 * 60_000;

/** Short code shown on both screens, to check it is the right device being added. */
export function deviceCode(id: string): string {
  return `${id.slice(0, 3)}-${id.slice(3, 6)}`.toUpperCase();
}

async function linkMesh(secret: string, wsUrl: (p: string) => string, iceServers: RTCIceServer[]): Promise<Mesh> {
  return new Mesh({
    url: wsUrl(`/ws/room/${await roomIdFor(`link:${secret}`)}`),
    iceServers,
    authKey: await deriveAuthKey(`link-v1:${secret}`),
  });
}

function parseRecord(raw: unknown): DeviceRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const b64 = (v: unknown) => typeof v === 'string' && /^[A-Za-z0-9_-]{43}$/.test(v);
  if (typeof r.id !== 'string' || !/^[0-9a-f]{32}$/.test(r.id) || !b64(r.signPub) || !b64(r.dhPub)) return null;
  return { id: r.id, name: safeLabel(r.name), signPub: r.signPub as string, dhPub: r.dhPub as string };
}

export type LinkRequest = { peer: string; device: DeviceRecord };

/** Events: 'request' {peer, device}, 'done' {device}, 'expired'. */
export class LinkHost extends EventTarget {
  readonly secret = randomSecret();
  #mesh: Mesh | null = null;
  #timer: ReturnType<typeof setTimeout> | undefined;
  request: LinkRequest | null = null;

  constructor(readonly space: Space) {
    super();
  }

  link(origin: string): string {
    return `${origin}/#/link/${this.secret}`;
  }

  async start(wsUrl: (p: string) => string, iceServers: RTCIceServer[]): Promise<void> {
    const mesh = await linkMesh(this.secret, wsUrl, iceServers);
    this.#mesh = mesh;
    mesh.addEventListener('ctl', (e) => {
      const { peer, msg } = (e as CustomEvent).detail as { peer: string; msg: { t?: string; device?: unknown } };
      const device = msg?.t === 'join' ? parseRecord(msg.device) : null;
      if (!device || this.request) return;
      this.request = { peer, device };
      this.dispatchEvent(new CustomEvent('request', { detail: this.request }));
    });
    await mesh.start();
    this.#timer = setTimeout(() => {
      this.stop();
      this.dispatchEvent(new Event('expired'));
    }, LINK_TTL_MS);
  }

  async accept(): Promise<void> {
    const req = this.request;
    if (!req || !this.#mesh) return;
    const sealed = await sealFor(this.space.device, req.device.dhPub, JSON.stringify(this.space.journal.space));
    this.#mesh.send(req.peer, { t: 'welcome', host: publicRecord(this.space.device), sealed });
    await this.space.journal.append({ type: 'device.add', ts: Date.now(), device: req.device });
    this.dispatchEvent(new CustomEvent('done', { detail: { device: req.device } }));
    // Give the message time to leave before tearing the connection down.
    setTimeout(() => this.stop(), 3000);
  }

  decline(): void {
    if (this.request) this.#mesh?.send(this.request.peer, { t: 'declined' });
    this.request = null;
  }

  stop(): void {
    clearTimeout(this.#timer);
    this.#mesh?.close();
    this.#mesh = null;
  }
}

/**
 * New-device side. Resolves with this device's fresh keys and the space secrets once the host
 * confirmed; rejects if it declined. `onStatus` reports 'connecting' | 'waiting'.
 */
export async function joinByLink(
  secret: string,
  name: string,
  wsUrl: (p: string) => string,
  iceServers: RTCIceServer[],
  onStatus: (s: 'connecting' | 'waiting', device: LocalDevice) => void,
): Promise<{ device: LocalDevice; secrets: SpaceSecrets; close: () => void }> {
  const device = await createDevice(name);
  const mesh = await linkMesh(secret, wsUrl, iceServers);
  onStatus('connecting', device);
  return new Promise((resolve, reject) => {
    mesh.addEventListener('authed', (e) => {
      mesh.send((e as CustomEvent).detail.peer, { t: 'join', device: publicRecord(device) });
      onStatus('waiting', device);
    });
    mesh.addEventListener('ctl', async (e) => {
      const { msg } = (e as CustomEvent).detail as { msg: { t?: string; host?: unknown; sealed?: { from: string; nonce: string; ct: string } } };
      if (msg?.t === 'declined') {
        mesh.close();
        reject(new Error('declined'));
      }
      if (msg?.t !== 'welcome') return;
      const host = parseRecord(msg.host);
      if (!host || !msg.sealed) return;
      try {
        const secrets = JSON.parse(await openSealed(device, host.dhPub, msg.sealed)) as SpaceSecrets;
        if (typeof secrets.spaceId !== 'string' || typeof secrets.epoch !== 'number' || !secrets.secrets?.[secrets.epoch]) throw new Error('bad secrets');
        resolve({ device, secrets, close: () => mesh.close() });
      } catch (err) {
        mesh.close();
        reject(err);
      }
    });
    mesh.start().catch(reject);
  });
}
